/**
 * The Hub Crookwell — Membership Terms & Conditions
 *
 * Adapted from the signed membership agreement. Changes from the original document:
 *  - Removed "Membership Particulars" references — booking details are now shown during checkout
 *  - Updated Section 2.1 / 3.1 to reflect actual membership types offered on the platform
 *  - Section 7 renamed to "Dedicated Office" to reflect the actual product
 *  - Clause 13.3 updated to reference account contact details instead of "Particulars"
 */

export const TERMS_SECTIONS = [
  {
    heading: '1. Overview & Structure',
    clauses: [
      {
        id: '1.1',
        title: 'What this is.',
        text: 'These Terms, your booking details (confirmed during checkout), and the House Rules (Annex A) together form the Agreement.',
      },
      {
        id: '1.2',
        title: 'No lease or tenancy.',
        text: 'The Agreement grants a personal, revocable licence to access and use shared facilities. It does not grant exclusive possession, creates no lease or tenancy, and confers no security of tenure or interest in land.',
      },
      {
        id: '1.3',
        title: 'Business use.',
        text: 'Membership is for business/professional use only and not residential use.',
      },
      {
        id: '1.4',
        title: 'Who you are.',
        text: 'If you sign on behalf of a company, you warrant you are authorised to bind that entity and you remain responsible for your employees and contractors (Authorised Users) and any guests.',
      },
    ],
  },
  {
    heading: '2. Services',
    clauses: [
      {
        id: '2.1',
        title: 'Included services.',
        text: 'Hot Desk memberships: Day Pass (single day) or Monthly Membership (rolling month-to-month); shared desks in common areas allocated on a hot-desk basis. Dedicated Office memberships: your own assigned desk on a monthly basis. Meeting Room: bookable by the hour, half-day or full day. Common areas: lounges, kitchen/amenities. Internet access: shared Wi-Fi (fair-use; see clause 8). Printing/scanning: where provided; usage fees may apply.',
      },
      {
        id: '2.2',
        title: 'Hours & access.',
        text: 'Standard access is 8am to 7pm on weekdays Monday to Friday (public holiday hours may vary). We may temporarily close areas for maintenance, events or safety.',
      },
      {
        id: '2.3',
        title: 'Variations.',
        text: 'Furniture layout and amenity levels may change over time. We\'ll give reasonable notice for material changes.',
      },
      {
        id: '2.4',
        title: 'Mail/parcel handling.',
        text: 'You may use our street address for mail delivery; however you are responsible for receiving, collecting and the security of your goods.',
      },
    ],
  },
  {
    heading: '3. Membership Options & Guests',
    clauses: [
      {
        id: '3.1',
        title: 'Membership options.',
        text: 'Available plans are: Hot Desk – Day Pass; Hot Desk – Monthly Membership; Dedicated Office – Monthly Membership; Meeting Room – Hourly, Half-Day, or Full Day.',
      },
      {
        id: '3.2',
        title: 'Guests.',
        text: 'Guests are permitted only for pre-booked meetings or as otherwise approved. You are responsible for your guests\' compliance. We may refuse entry to any person for safety or operational reasons.',
      },
      {
        id: '3.3',
        title: 'Access devices.',
        text: 'Keys and access credentials remain our property and must not be shared or copied. Lost or stolen keys must be reported immediately; replacement fees may apply.',
      },
    ],
  },
  {
    heading: '4. Fees, Invoicing & Taxes',
    clauses: [
      {
        id: '4.1',
        title: 'Billing.',
        text: 'Fees are payable in advance from the Start Date shown in your booking confirmation. Usage-based charges (e.g., meeting room overages, printing, damages) are billed in arrears.',
      },
      {
        id: '4.2',
        title: 'Late payment.',
        text: 'Unpaid amounts may incur a reasonable late fee or interest at the lesser of 10% p.a. or the maximum permitted by law. We may suspend access until amounts are paid.',
      },
      {
        id: '4.3',
        title: 'Price changes.',
        text: 'We may update recurring fees on 30 days\' notice (effective from the next billing cycle after notice).',
      },
      {
        id: '4.4',
        title: 'Taxes.',
        text: 'All amounts are exclusive of GST unless stated otherwise. GST (and any other applicable taxes/levies) will be added and shown on a valid tax invoice.',
      },
      {
        id: '4.5',
        title: 'Refunds.',
        text: 'Fees are non-refundable, except as required by law.',
      },
    ],
  },
  {
    heading: '5. Term, Renewal & Termination',
    clauses: [
      {
        id: '5.1',
        title: 'Rolling memberships.',
        text: 'Continue on a rolling basis for the period set out in your booking confirmation until terminated by either party on at least 14 days\' written notice prior to the end of a billing month.',
      },
      {
        id: '5.2',
        title: 'Fixed Term memberships.',
        text: 'You are committed for the specified term. Early termination by you requires payment of all fees for the remainder of the term, unless otherwise agreed in writing.',
      },
      {
        id: '5.3',
        title: 'Breach & safety.',
        text: 'We may suspend or terminate immediately for: non-payment; material breach; unsafe, unlawful or disruptive conduct; or unauthorised access/sharing of credentials.',
      },
      {
        id: '5.4',
        title: 'Effect of termination.',
        text: 'On termination or expiry you must: stop using the premises/services; return all access devices; remove your property; and settle all amounts due. Property left behind may be treated as abandoned after reasonable notice.',
      },
    ],
  },
  {
    heading: '6. Space Use & House Rules',
    clauses: [
      {
        id: '6.1',
        title: 'Respectful use.',
        text: 'You must comply with the House Rules (Annex A) and reasonable directions of our staff.',
      },
      {
        id: '6.2',
        title: 'Prohibited activities.',
        text: 'No unlawful, offensive, dangerous or disruptive activities; no storage or use of hazardous materials; no smoking or vaping inside; no alterations to walls, fixtures, cabling or IT without our written consent.',
      },
      {
        id: '6.3',
        title: 'Pets.',
        text: 'Pets only with prior approval and subject to behaviour, hygiene and insurance requirements. You are liable for any pet-related damage or injury.',
      },
    ],
  },
  {
    heading: '7. Dedicated Office',
    clauses: [
      {
        id: '7.1',
        title: 'Fit-out & furniture.',
        text: 'Dedicated desks are provided furnished unless otherwise agreed. Any additional fit-out or modifications require our written approval and must be removed at your cost (and the area made good) unless we agree to keep them.',
      },
      {
        id: '7.2',
        title: 'Condition.',
        text: 'You must keep your desk area reasonably clean and in good condition, avoiding damage beyond fair wear and tear.',
      },
      {
        id: '7.3',
        title: 'Keys & access.',
        text: 'Where keys or lockable storage are provided, they must not be copied or shared. Lock changes are prohibited without our approval.',
      },
    ],
  },
  {
    heading: '8. Internet & IT',
    clauses: [
      {
        id: '8.1',
        title: 'Shared service.',
        text: 'Internet is a shared resource provided on a fair-use, best-efforts basis. Availability and speed may vary.',
      },
      {
        id: '8.2',
        title: 'Acceptable use.',
        text: 'No unlawful content/activity, network abuse, high-risk hosting, cryptocurrency mining, spamming, or interference with others\' use. We may block malicious traffic or temporarily disconnect devices that threaten network security.',
      },
      {
        id: '8.3',
        title: 'Security.',
        text: 'You are responsible for securing your devices (e.g., updates, anti-malware, encryption). We are not responsible for unauthorised access to, or loss of, your data.',
      },
    ],
  },
  {
    heading: '9. Privacy',
    clauses: [
      {
        id: '9.1',
        title: 'Collection.',
        text: 'We collect and handle personal information in accordance with our Privacy Notice and the Privacy Act 1988 (Cth). CCTV may operate in common areas for security.',
      },
      {
        id: '9.2',
        title: 'Contacting you.',
        text: 'You consent to us contacting you about your membership, invoices, service updates and building matters.',
      },
    ],
  },
  {
    heading: '10. Property, Risk & Insurance',
    clauses: [
      {
        id: '10.1',
        title: 'Your property.',
        text: 'You are responsible for your equipment and belongings. We are not liable for loss, theft or damage to your property on the premises, except to the extent caused by our negligence or wilful misconduct.',
      },
      {
        id: '10.2',
        title: 'Insurance.',
        text: 'You should maintain appropriate insurance for your business (e.g., public liability, contents). We may require evidence of insurance for certain memberships or events.',
      },
      {
        id: '10.3',
        title: 'Damage.',
        text: 'You are responsible for damage you (or your users/guests/pets) cause to the premises, furniture, or equipment (beyond fair wear and tear).',
      },
    ],
  },
  {
    heading: '11. Compliance & Safety',
    clauses: [
      {
        id: '11.1',
        title: 'Laws & building rules.',
        text: 'You must comply with applicable Australian and NSW laws and building/strata/landlord requirements notified to you.',
      },
      {
        id: '11.2',
        title: 'Health & safety.',
        text: 'Follow safety signage and instructions. Do not obstruct exits or tamper with life-safety systems. Report hazards or incidents promptly.',
      },
    ],
  },
  {
    heading: '12. Liability & Indemnity',
    clauses: [
      {
        id: '12.1',
        title: 'Australian Consumer Law.',
        text: 'Nothing in this Agreement excludes, restricts or modifies any Non-Excludable Rights under the Australian Consumer Law. Where our liability for breach of a guarantee can be limited, it is limited to supplying the services again or paying the cost of having the services supplied again.',
      },
      {
        id: '12.2',
        title: 'Cap and exclusions.',
        text: 'To the extent permitted by law, our total aggregate liability arising out of or in connection with the Agreement is limited to the fees you paid to us in the 12 months before the event giving rise to liability. We are not liable for indirect or consequential loss, loss of profit, revenue, or business interruption, except to the extent such limitation is not permitted by law.',
      },
      {
        id: '12.3',
        title: 'Indemnity.',
        text: 'You indemnify us against all claims, demands, actions, losses, costs and liabilities (including those brought by third parties) arising from: (a) your breach of the Agreement; (b) your users\' or guests\' acts or omissions (including personal injury, such as where a guest trips or suffers harm while attending the premises); or (c) your events, fit-out or use of the space — except to the extent caused by our negligence or wilful misconduct.',
      },
    ],
  },
  {
    heading: '13. General',
    clauses: [
      {
        id: '13.1',
        title: 'Changes.',
        text: 'We may update these Terms by giving 30 days\' notice. For rolling memberships, continued use after the effective date constitutes acceptance. For fixed term agreements, changes take effect from the next renewal.',
      },
      {
        id: '13.2',
        title: 'Assignment.',
        text: 'You must not assign or transfer the Agreement without our consent. We may assign on notice.',
      },
      {
        id: '13.3',
        title: 'Notices.',
        text: 'Given by email to the contact details registered in your account and deemed received when sent (unless bounce-back). You must keep your contact details current.',
      },
      {
        id: '13.4',
        title: 'Force majeure.',
        text: 'We are not liable for delay or failure due to events beyond our reasonable control (e.g., utility outages, government orders, emergencies, supply issues).',
      },
      {
        id: '13.5',
        title: 'Governing law.',
        text: 'The Agreement is governed by the laws of New South Wales, Australia. The parties submit to the non-exclusive jurisdiction of NSW courts. Before commencing proceedings (other than urgent relief), the parties will use good-faith discussions and consider mediation (e.g., via the NSW Small Business Commissioner).',
      },
      {
        id: '13.6',
        title: 'Severability & waiver.',
        text: 'Invalid terms are severed; the remainder continues. A failure to enforce is not a waiver.',
      },
      {
        id: '13.7',
        title: 'Entire agreement.',
        text: 'This Agreement supersedes prior discussions regarding its subject matter.',
      },
    ],
  },
];

export const HOUSE_RULES = [
  { id: 'A1', title: 'Be considerate.', text: 'Keep noise to a reasonable level; use phone booths/meeting rooms for calls where possible.' },
  { id: 'A2', title: 'Clean & tidy.', text: 'Clear desks after use; wash up dishes; use bins provided.' },
  { id: 'A3', title: 'Security.', text: 'Do not tailgate or let others use your access. Report lost keys immediately.' },
  { id: 'A4', title: 'Health & safety.', text: 'Follow posted instructions; do not block exits or tamper with equipment.' },
  { id: 'A5', title: 'No nuisance.', text: 'No illegal, offensive or dangerous conduct; no hazardous substances.' },
  { id: 'A6', title: 'Guests.', text: 'Register guests; accompany them at all times; you\'re responsible for their conduct.' },
  { id: 'A7', title: 'IT use.', text: 'Fair-use only; no spamming, malware, high-risk hosting, or network abuse.' },
  { id: 'A8', title: 'Pets.', text: 'Only if pre-approved; keep controlled and clean; you\'re responsible for any damage.' },
  { id: 'A9', title: 'Meeting rooms.', text: 'Use only during bookings; vacate on time; charges apply for overruns.' },
  { id: 'A10', title: 'Filming & signage.', text: 'Prior approval required for promotional filming or external signage.' },
];
